import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Check, ChevronsUpDown, Info, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StorageAreaHint = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" data-testid="storage-area-hint-icon" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] text-xs">
        This sets storage area going forward — to reassign it later, use Master Item List.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ExistingItemSearch = ({ authHeaders, onSelect, testIdPrefix, initialQuery }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);

  const runSearch = useCallback(async (q) => {
    try {
      const res = await axios.get(`${API}/inventory/items/search`, { params: { q }, headers: authHeaders });
      setResults(res.data.items || []);
    } catch {
      setResults([]);
    }
  }, [authHeaders]);

  useEffect(() => {
    const t = setTimeout(() => runSearch(query), 250);
    return () => clearTimeout(t);
  }, [query, runSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-56 justify-between" data-testid={`${testIdPrefix}-trigger`}>
          {selected ? selected.canonicalName : "Search existing items..."}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type to search..." value={query} onValueChange={setQuery}
            data-testid={`${testIdPrefix}-input`} />
          <CommandList>
            <CommandEmpty>No matching items</CommandEmpty>
            <CommandGroup>
              {results.map((item) => (
                <CommandItem key={item.id} value={item.canonicalName} data-testid={`${testIdPrefix}-option-${item.id}`}
                  onSelect={() => { setSelected(item); onSelect(item); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${selected?.id === item.id ? "opacity-100" : "opacity-0"}`} />
                  {item.canonicalName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const MenuMatchEntryRow = ({ entry, items, month, year, authHeaders, onResolved, storageAreas }) => {
  const [mode, setMode] = useState(null); // "select_existing" | "add_new" | "split_dish"
  const [selectedItem, setSelectedItem] = useState(null);
  const [newName, setNewName] = useState(entry.rawText);
  const [newArea, setNewArea] = useState("unassigned");
  const [splitRows, setSplitRows] = useState([{ name: "", area: "unassigned" }]);
  const [busy, setBusy] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);
  const [pendingWarning, setPendingWarning] = useState(null); // {message, duplicateOfRawText} — split_dish only, set before anything is saved

  const areas = storageAreas || [];
  const suggestion = items.find((i) => i.id === entry.suggestedItemId && i.active && !i.mergedIntoItemId);

  const dismissEntry = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/inventory/menu/matches/resolve`, {
        rawText: entry.rawText, action: "dismiss",
      }, { params: { month, year }, headers: authHeaders });
      toast.success("Duplicate entry removed");
      onResolved(res.data.match_record);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not dismiss this entry");
    } finally {
      setBusy(false);
    }
  };

  const resolve = async (payload) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/inventory/menu/matches/resolve`, {
        rawText: entry.rawText, ...payload,
      }, { params: { month, year }, headers: authHeaders });
      if (res.data.warning) {
        // Nothing was saved — this entry stays in Needs Review until the director explicitly
        // picks one of the two choices below, so a missed warning can never leave a duplicate
        // silently counted.
        setPendingWarning(res.data.warning);
        onResolved(res.data.match_record);
        return;
      }
      setPendingWarning(null);
      onResolved(res.data.match_record);
      toast.success(payload.action === "dismiss" ? "Removed" : "Resolved");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to resolve item";
      if (payload.action === "add_new" && detail.toLowerCase().includes("already exists")) {
        // Dead-end fix: instead of leaving just an error toast, switch straight into the
        // "Match to existing item" search, pre-filled with the name that collided.
        setMode("select_existing");
        toast.error(`${detail} Pick it from the search below.`);
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const updateSplitRow = (idx, field, value) => {
    setSplitRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const addSplitRow = () => setSplitRows((rows) => [...rows, { name: "", area: "unassigned" }]);
  const removeSplitRow = (idx) => setSplitRows((rows) => rows.filter((_, i) => i !== idx));
  const validSplitRows = splitRows.filter((r) => r.name.trim());
  const cancelMode = () => { setPendingWarning(null); setMode(suggestion ? null : "choose"); };
  const keepAsSeparateDish = () => resolve({
    action: "split_dish",
    splitItemNames: validSplitRows.map((r) => r.name.trim()),
    splitItemStorageAreas: validSplitRows.map((r) => r.area),
    confirmOverlap: true,
  });

  return (
    <div data-testid={`review-entry-${entry.rawText}`} className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium text-slate-800">"{entry.rawText}"</p>

      {suggestion && mode === null && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-600">Did you mean "{suggestion.canonicalName}"?</span>
          <Button size="sm" data-testid={`confirm-suggestion-${entry.rawText}`} disabled={busy}
            onClick={() => resolve({ action: "confirm_match", itemId: suggestion.id })}>
            Yes, same item
          </Button>
          <Button size="sm" variant="outline" onClick={() => setMode("choose")}>Not quite</Button>
        </div>
      )}

      {(!suggestion || mode === "choose") && mode !== "select_existing" && mode !== "add_new" && mode !== "split_dish" && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button size="sm" variant="outline" data-testid={`mode-select-existing-${entry.rawText}`} onClick={() => setMode("select_existing")}>
            Match to existing item
          </Button>
          <Button size="sm" variant="outline" data-testid={`mode-add-new-${entry.rawText}`} onClick={() => setMode("add_new")}>
            Add as new item
          </Button>
          <Button size="sm" variant="outline" data-testid={`mode-split-dish-${entry.rawText}`} onClick={() => setMode("split_dish")}>
            Split into ingredients
          </Button>
          {!confirmDismiss ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" data-testid={`mode-dismiss-${entry.rawText}`} onClick={() => setConfirmDismiss(true)}
                    className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[200px]">
                  Remove from review. It won't count toward shopping needs.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-red-700">
              Remove this entry — already counted under another name?
              <Button size="sm" variant="destructive" disabled={busy} data-testid={`confirm-dismiss-${entry.rawText}`}
                onClick={dismissEntry}>
                Yes, remove
              </Button>
              <Button size="sm" variant="ghost" data-testid={`cancel-dismiss-${entry.rawText}`} onClick={() => setConfirmDismiss(false)}>
                Cancel
              </Button>
            </span>
          )}
        </div>
      )}

      {mode === "select_existing" && (
        <div className="flex gap-2">
          <ExistingItemSearch authHeaders={authHeaders} onSelect={setSelectedItem} testIdPrefix={`existing-search-${entry.rawText}`}
            initialQuery={newName} />
          <Button size="sm" disabled={!selectedItem || busy} data-testid={`confirm-select-existing-${entry.rawText}`}
            onClick={() => resolve({ action: "select_existing", itemId: selectedItem.id })}>
            Confirm
          </Button>
          <Button size="sm" variant="ghost" data-testid={`cancel-mode-${entry.rawText}`} onClick={cancelMode}>Cancel</Button>
        </div>
      )}

      {mode === "add_new" && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-56" data-testid={`new-item-name-${entry.rawText}`} />
            <Select value={newArea} onValueChange={setNewArea}>
              <SelectTrigger className="w-40" data-testid={`new-item-area-${entry.rawText}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <StorageAreaHint />
            <Button size="sm" disabled={!newName.trim() || busy} data-testid={`add-new-item-save-${entry.rawText}`}
              onClick={() => resolve({ action: "add_new", newItemName: newName.trim(), storageArea: newArea })}>
              Add Item
            </Button>
            <Button size="sm" variant="ghost" data-testid={`cancel-mode-${entry.rawText}`} onClick={cancelMode}>Cancel</Button>
          </div>
        </div>
      )}

      {mode === "split_dish" && !pendingWarning && (
        <div className="space-y-2">
          <div className="space-y-1.5">
            {splitRows.map((row, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input placeholder="e.g. Ground Beef" value={row.name}
                  onChange={(e) => updateSplitRow(idx, "name", e.target.value)}
                  className="w-56" data-testid={`split-dish-name-${entry.rawText}-${idx}`} />
                <Select value={row.area} onValueChange={(v) => updateSplitRow(idx, "area", v)}>
                  <SelectTrigger className="w-40" data-testid={`split-dish-area-${entry.rawText}-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {idx === 0 && <StorageAreaHint />}
                {splitRows.length > 1 && (
                  <button type="button" onClick={() => removeSplitRow(idx)} data-testid={`split-dish-remove-${entry.rawText}-${idx}`}
                    className="p-1 rounded hover:bg-amber-100 text-slate-400 hover:text-slate-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addSplitRow} data-testid={`split-dish-add-row-${entry.rawText}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add another ingredient
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" disabled={validSplitRows.length === 0 || busy} data-testid={`split-dish-save-${entry.rawText}`}
              onClick={() => resolve({
                action: "split_dish",
                splitItemNames: validSplitRows.map((r) => r.name.trim()),
                splitItemStorageAreas: validSplitRows.map((r) => r.area),
              })}>
              Save Ingredients
            </Button>
            <Button size="sm" variant="ghost" data-testid={`cancel-mode-${entry.rawText}`} onClick={cancelMode}>Cancel</Button>
          </div>
          <p className="text-xs text-slate-400" data-testid={`split-dish-help-${entry.rawText}`}>
            Add as many ingredients as you need — each one becomes its own item to count and shop
            for. The dish name won't become its own line item, but it will show as a "Used in"
            reference next to each ingredient on the Shopping List.
          </p>
        </div>
      )}

      {mode === "split_dish" && pendingWarning && (
        <div className="space-y-2 border border-amber-300 bg-amber-100/70 rounded-lg p-3" data-testid={`split-dish-warning-${entry.rawText}`}>
          <p className="text-sm text-amber-900 flex items-start gap-1.5">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {pendingWarning.message}
          </p>
          <p className="text-xs text-amber-700">Nothing has been saved yet — pick one:</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="destructive" disabled={busy} data-testid={`dismiss-duplicate-${entry.rawText}`}
              onClick={dismissEntry}>
              Dismiss — it's the same as "{pendingWarning.duplicateOfRawText}"
            </Button>
            <Button size="sm" variant="outline" disabled={busy} data-testid={`keep-separate-dish-${entry.rawText}`}
              onClick={keepAsSeparateDish}>
              Keep as separate dish
            </Button>
            <Button size="sm" variant="ghost" data-testid={`edit-ingredients-${entry.rawText}`}
              onClick={() => setPendingWarning(null)}>
              Edit ingredients
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuMatchEntryRow;
